/**
 * A re-import's merge run in a DOCX worker of its own (part 23b, principle 6; table V′3): the job posted, progress passed
 * on, the merged chapters given back once, and the worker ended at the first of its reply, an error, a message that isn't
 * one, or a cancel. The worker is injected, so the logic runs in tests.
 */
import type { MergedChapter } from '@cockatrice/core';
import type { DocxWorker } from '../docx/controller.ts';
import { readMergeReply } from '../docx/protocol.ts';
import type { MergeJob, SourcesJob } from '../docx/protocol.ts';

/** A message event's data; undefined for anything else. */
function dataOf(event: unknown): unknown {
  return typeof event === 'object' && event !== null ? Reflect.get(event, 'data') : undefined;
}

/**
 * V′3, V′4: `job` (a re-import's, or part 24d's Markdown merges) merged in a worker `start` makes: one merged chapter per
 * chapter asked, in order; null when the worker
 * can't start, fails, sends what isn't a merge reply or another count of chapters, or `signal` cancels it.
 */
export function runMerge(start: () => DocxWorker, job: MergeJob | SourcesJob, onProgress: (done: number, total: number) => void, signal: AbortSignal): Promise<readonly MergedChapter[] | null> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve(null);
      return;
    }
    let worker: DocxWorker;
    try {
      worker = start();
    } catch {
      resolve(null);
      return;
    }
    let ended = false;
    const end = (chapters: readonly MergedChapter[] | null): void => {
      if (ended) return;
      ended = true;
      signal.removeEventListener('abort', cancel);
      worker.terminate();
      resolve(chapters);
    };
    const cancel = (): void => {
      end(null);
    };
    signal.addEventListener('abort', cancel);
    worker.addEventListener('error', cancel);
    worker.addEventListener('messageerror', cancel);
    // A terminated worker sends nothing more, so every message comes before the end.
    worker.addEventListener('message', (event) => {
      const reply = readMergeReply(dataOf(event));
      if (reply?.kind === 'progress') onProgress(reply.done, reply.total);
      else end(reply?.kind === 'merged' && reply.chapters.length === job.chapters.length ? reply.chapters : null);
    });
    try {
      worker.postMessage(job);
    } catch {
      end(null);
    }
  });
}
